import type { SubscriptionTier } from '../constants/planFeatures';
import { TierFeatureKey } from '../constants/planFeatures';
import type { AcademyOperatingProfileInput } from '../contracts/academyOnboarding';

export const PLAN_RECOMMENDATION_REASON_CODES = [
  'multiple_coaches_need_portals',
  'students_need_accounts',
  'students_initiate_bookings',
  'online_payments',
  'waitlists_or_makeup',
  'multi_location',
  'academy_admins',
  'coach_invites',
  'automation_workflows',
  'student_limit_starter',
] as const;

export type PlanRecommendationReasonCode = (typeof PLAN_RECOMMENDATION_REASON_CODES)[number];

export interface PlanRecommendationResult {
  recommendedTier: SubscriptionTier;
  reasonCodes: PlanRecommendationReasonCode[];
  /** Human-readable reasons for UI (no PII). */
  reasonLabels: string[];
}

const REASON_LABELS: Record<PlanRecommendationReasonCode, string> = {
  multiple_coaches_need_portals: 'Coach logins and dashboards for your staff',
  students_need_accounts: 'Student and guardian accounts on your academy site',
  students_initiate_bookings: 'Students or guardians booking lessons themselves',
  online_payments: 'Online card payments at checkout',
  waitlists_or_makeup: 'Waitlists or make-up credit self-service',
  multi_location: 'Multiple physical locations',
  academy_admins: 'Additional academy administrators',
  coach_invites: 'Inviting coaches to manage their own schedule',
  automation_workflows: 'Automated reminders and advanced workflows',
  student_limit_starter: 'More than 100 active students (Starter cap)',
};

function pushReason(
  codes: PlanRecommendationReasonCode[],
  code: PlanRecommendationReasonCode
): void {
  if (!codes.includes(code)) codes.push(code);
}

/**
 * Pure rule: recommend Professional when declared needs require Professional-tier features.
 */
export function recommendSubscriptionTier(
  profile: Partial<AcademyOperatingProfileInput>
): PlanRecommendationResult {
  const reasonCodes: PlanRecommendationReasonCode[] = [];

  if (profile.coachesNeedPortalAccess === true) {
    pushReason(reasonCodes, 'coach_invites');
    pushReason(reasonCodes, 'multiple_coaches_need_portals');
  }

  const coachBand = profile.coachCountBand;

  if (profile.studentsNeedAccounts === true) {
    pushReason(reasonCodes, 'students_need_accounts');
  }

  if (profile.studentsInitiateBookings === true) {
    pushReason(reasonCodes, 'students_initiate_bookings');
  }

  if (profile.needsOnlinePayments === true) {
    pushReason(reasonCodes, 'online_payments');
  }

  const workflows = profile.desiredWorkflows ?? [];
  if (
    workflows.includes('waitlists') ||
    workflows.includes('makeup_credits') ||
    workflows.includes('online_payments') ||
    workflows.includes('student_self_booking') ||
    workflows.includes('student_portals') ||
    workflows.includes('coach_portals')
  ) {
    if (workflows.includes('waitlists') || workflows.includes('makeup_credits')) {
      pushReason(reasonCodes, 'waitlists_or_makeup');
    }
    if (workflows.includes('online_payments')) {
      pushReason(reasonCodes, 'online_payments');
    }
    if (workflows.includes('student_self_booking')) {
      pushReason(reasonCodes, 'students_initiate_bookings');
    }
    if (workflows.includes('student_portals')) {
      pushReason(reasonCodes, 'students_need_accounts');
    }
    if (workflows.includes('coach_portals')) {
      pushReason(reasonCodes, 'multiple_coaches_need_portals');
    }
  }

  if (profile.locationCountBand === '2-3' || profile.locationCountBand === '4+') {
    pushReason(reasonCodes, 'multi_location');
  }

  if (profile.operatingRole === 'owner_manager' && coachBand !== '0' && coachBand !== '1') {
    pushReason(reasonCodes, 'academy_admins');
  }

  if (profile.sizeBand === '300+') {
    pushReason(reasonCodes, 'student_limit_starter');
  }

  const recommendedTier: SubscriptionTier =
    reasonCodes.length > 0 ? 'professional' : 'starter';

  return {
    recommendedTier,
    reasonCodes,
    reasonLabels: reasonCodes.map((c) => REASON_LABELS[c]),
  };
}

/** Always-unavailable Starter capabilities shown in onboarding/limitation copy. */
export const STARTER_BASELINE_LIMITATIONS: readonly string[] = [
  'Coach login accounts and dashboards',
  'Student and guardian member accounts and dashboards',
  'Self-service account registration and online checkout',
  'Online card payments at lesson checkout',
] as const;

/** Features unavailable on Starter when recommendation says Professional. */
export function starterUnavailableFeaturesFromReasons(
  reasonCodes: readonly PlanRecommendationReasonCode[]
): string[] {
  const labels = new Set<string>(STARTER_BASELINE_LIMITATIONS);
  for (const code of reasonCodes) {
    labels.add(REASON_LABELS[code]);
  }
  return [...labels];
}

/** Map reason codes to tier feature keys for entitlement checks in UI. */
export function reasonCodesToTierFeatures(
  codes: readonly PlanRecommendationReasonCode[]
): TierFeatureKey[] {
  const features = new Set<TierFeatureKey>();
  for (const code of codes) {
    switch (code) {
      case 'online_payments':
        features.add(TierFeatureKey.PAYMENT_PROCESSING);
        break;
      case 'waitlists_or_makeup':
        features.add(TierFeatureKey.WAITLISTS);
        features.add(TierFeatureKey.MAKEUP_CREDITS);
        break;
      case 'students_need_accounts':
      case 'students_initiate_bookings':
        features.add(TierFeatureKey.STUDENT_GUARDIAN_PORTAL);
        features.add(TierFeatureKey.STUDENT_SELF_SERVICE_BOOKING);
        break;
      case 'multiple_coaches_need_portals':
      case 'coach_invites':
        features.add(TierFeatureKey.COACH_PORTAL);
        features.add(TierFeatureKey.COACH_INVITES);
        break;
      case 'multi_location':
        features.add(TierFeatureKey.MULTI_LOCATION);
        break;
      case 'academy_admins':
        features.add(TierFeatureKey.ACADEMY_ADMINS);
        break;
      default:
        break;
    }
  }
  return [...features];
}

export const CLUB_PLAN_RECOMMENDATION_REASON_CODES = ['branded_subdomain'] as const;

export type ClubPlanRecommendationReasonCode =
  (typeof CLUB_PLAN_RECOMMENDATION_REASON_CODES)[number];

export interface ClubPlanRecommendationResult {
  recommendedTier: import('../constants/planFeatures').ClubPlanTier;
  reasonCodes: ClubPlanRecommendationReasonCode[];
  reasonLabels: string[];
}

const CLUB_REASON_LABELS: Record<ClubPlanRecommendationReasonCode, string> = {
  branded_subdomain: 'Branded subdomain (yourclub.courtsync.io)',
};

/**
 * Pure rule: recommend Club Pro only when branded subdomain is required.
 * Online payments and multiple locations are included on Club (starter).
 */
export function recommendClubPlan(
  profile: Partial<import('../contracts/clubOnboarding').ClubOperatingProfileInput>
): ClubPlanRecommendationResult {
  const reasonCodes: ClubPlanRecommendationReasonCode[] = [];

  if (profile.needsBrandedSubdomain === true) {
    reasonCodes.push('branded_subdomain');
  }

  const recommendedTier = reasonCodes.length > 0 ? 'professional' : 'starter';

  return {
    recommendedTier,
    reasonCodes,
    reasonLabels: reasonCodes.map((c) => CLUB_REASON_LABELS[c]),
  };
}
